import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, MARKETING } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import * as S from './Promotions.styled';

const PROMO_CODE_TYPES = {
	freeCredit: 'freeCredit',
	buyXGetXFree: 'buyXGetXFree',
	freeSubscription: 'freeSubscription',
	subscriptionDiscount: 'subscriptionDiscount'
};

const defaultColSpan = {
	md: 6
};

/**
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.modalName]
 * @param {'view' | 'edit' | 'create'} [props.mode = 'view']
 * @returns {JSX.Element}
 * @constructor
 * @description PromoCodeForm
 **/
const PromoCodeForm = NiceModal.create(({ id, modalName, mode }) => {
	const modal = useModal();
	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	const isViewMode = useMemo(() => mode === 'view', [mode]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				promoCode: yup.string().nullable().required(),
				promoCodeType: yup.string().nullable().required(),
				creditType: yup
					.string()
					.nullable()
					.when('promoCodeType', {
						is: (value) =>
							[
								PROMO_CODE_TYPES.freeCredit,
								PROMO_CODE_TYPES.buyXGetXFree,
								PROMO_CODE_TYPES.subscriptionDiscount
							].includes(value),
						then: (schema) => schema.required()
					}),
				pageAvailable: yup.string().nullable(),
				validFrom: yup.string().nullable().required(),
				expiry: yup.string().nullable().required(),
				oneTime: yup.boolean().nullable().required(),
				usageLimit: yup.number().nullable(),
				freeCreditAmount: yup.lazy(() =>
					yup
						.number()
						.nullable()
						.when(['promoCodeType', 'percentage'], {
							is: (promoCodeType, percentage) =>
								promoCodeType === PROMO_CODE_TYPES.freeCredit ||
								(PROMO_CODE_TYPES.subscriptionDiscount === promoCodeType && !percentage),
							then: (schema) => schema.required()
						})
				),
				creditExpiryDate: yup.string().nullable(),
				creditExpiryDays: yup.string().nullable(),
				subscriptionFreeDays: yup
					.number()
					.nullable()
					.when('promoCodeType', {
						is: (value) => PROMO_CODE_TYPES.freeSubscription === value,
						then: (schema) => schema.required()
					}),
				subscriptionPaymentType: yup
					.string()
					.nullable()
					.when('promoCodeType', {
						is: (value) =>
							[PROMO_CODE_TYPES.freeSubscription, PROMO_CODE_TYPES.subscriptionDiscount].includes(value),
						then: (schema) => schema.required()
					}),
				clientRestriction: yup.boolean().nullable(),
				clientPickListTitle: yup
					.array()
					.nullable()
					.when('clientRestriction', {
						is: true,
						then: (schema) => schema.required()
					}),
				packagePickListTitle: yup.array().nullable(),
				percentage: yup.lazy(() =>
					yup
						.number()
						.min(0)
						.max(100)
						.nullable()
						.when(['promoCodeType', 'freeCreditAmount'], {
							is: (promoCodeType, freeCreditAmount) =>
								PROMO_CODE_TYPES.subscriptionDiscount === promoCodeType && !freeCreditAmount,
							then: (schema) => schema.required()
						})
				),
				minCreditPurchase: yup.number().nullable(),
				maxFreeCredit: yup.number().nullable()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm()
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const promoCodeType = watch('promoCodeType');

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const fillForm = async () => {
		const defaultValues = {
			promoCode: null,
			promoCodeType: null,
			creditType: null,
			pageAvailable: null,
			validFrom: null,
			expiry: null,
			oneTime: false,
			usageLimit: null,
			freeCreditAmount: null,
			creditExpiryDate: null,
			creditExpiryDays: null,
			subscriptionFreeDays: null,
			clientRestriction: false,
			clientPickListTitle: null,
			subscriptionPaymentType: null,
			packagePickListTitle: null,
			percentage: null,
			minCreditPurchase: null,
			maxFreeCredit: null
		};

		if (!id) {
			return defaultValues;
		}

		try {
			const { data } = await axiosPrivate.post(MARKETING.GET_PROMO_CODE_DETAILS, { id });
			return {
				...defaultValues,
				...data,
				packagePickListTitle: data.packagePickListTitle?.split?.(',') ?? null,
				clientPickListTitle: data.clientPickListTitle?.split?.(',') ?? null
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
			return defaultValues;
		}
	};

	const preparePayload = useCallback((data) => {
		const payload = {
			promoCode: data.promoCode,
			promoCodeType: data.promoCodeType,
			pageAvailable: data.pageAvailable,
			validFrom: data.validFrom,
			expiry: data.expiry,
			oneTime: data.oneTime,
			usageLimit: data.usageLimit,
			clientRestriction: data.clientRestriction,
			clientPickListTitle: data.clientRestriction ? data.clientPickListTitle : null,
			packagePickListTitle: data.packagePickListTitle,
			id
		};
		switch (data.promoCodeType) {
			case PROMO_CODE_TYPES.freeCredit:
				return {
					...payload,
					creditType: data.creditType,
					freeCreditAmount: data.freeCreditAmount,
					creditExpiryDate: data.creditExpiryDate,
					creditExpiryDays: data.creditExpiryDate ? null : data.creditExpiryDays
				};
			case PROMO_CODE_TYPES.buyXGetXFree:
				return {
					...payload,
					creditType: data.creditType,
					percentage: data.percentage,
					minCreditPurchase: data.minCreditPurchase,
					creditExpiryDate: data.creditExpiryDate,
					creditExpiryDays: data.creditExpiryDate ? null : data.creditExpiryDays,
					maxFreeCredit: data.maxFreeCredit
				};
			case PROMO_CODE_TYPES.freeSubscription:
				return {
					...payload,
					creditExpiryDate: data.creditExpiryDate,
					creditExpiryDays: data.creditExpiryDate ? null : data.creditExpiryDays,
					subscriptionFreeDays: data.subscriptionFreeDays,
					subscriptionPaymentType: data.subscriptionPaymentType
				};
			case PROMO_CODE_TYPES.subscriptionDiscount:
				return {
					...payload,
					creditType: data.creditType,
					freeCreditAmount: data.freeCreditAmount,
					percentage: data.percentage,
					subscriptionFreeDays: data.subscriptionFreeDays,
					subscriptionPaymentType: data.subscriptionPaymentType
				};
			default:
				return null;
		}
	}, []);

	const onSubmit = async (data) => {
		const apiPath = mode === 'edit' ? MARKETING.UPDATE_PROMO_CODE : MARKETING.CREATE_PROMO_CODE;
		const payload = preparePayload(data);
		await axiosPrivate
			.post(apiPath, payload)
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.PROMOTIONS));
					showSuccess();
					handleCloseModal();
				}
			})
			.catch((error) => {
				if (error.response?.data.code === 'error.PRC002') {
					showError({
						message: 'error.promoCodeAlreadyExists'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	const modalTitle = useMemo(() => {
		switch (mode) {
			case 'create':
				return 'createPromoCode';
			case 'edit':
				return 'editPromoCode';
			default:
				return 'promoCodeDetails';
		}
	}, [mode]);

	return (
		<Modal fullwidth name={modalName} className="promo-code-modal" maxWidth="medium">
			<ModalTitle>
				<Typography variant="p1">{modalTitle}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<S.FormSubHeader>
						<Typography fontSize="1.8rem" fontWeight="600">
							creditDetails
						</Typography>
					</S.FormSubHeader>
					<FormItem colSpan={defaultColSpan} label="promoCode" isLoading={isLoading} required>
						<StringInput name="promoCode" placeholder="promoCode" viewMode={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="promoCodeType" isLoading={isLoading} required>
						<EnumSelect
							name="promoCodeType"
							enumName={ENUMS.PROMO_CODE_TYPE}
							setValue={(value) => {
								setValue(`promoCodeType`, value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch(`promoCodeType`)}
							viewMode={isViewMode}
						/>
					</FormItem>

					{[
						PROMO_CODE_TYPES.freeCredit,
						PROMO_CODE_TYPES.buyXGetXFree,
						PROMO_CODE_TYPES.subscriptionDiscount
					].includes(promoCodeType) && (
						<FormItem colSpan={defaultColSpan} label="creditType" isLoading={isLoading} required>
							<EnumSelect
								name="creditType"
								enumName={ENUMS.PROMO_CODE_CREDIT_TYPE}
								setValue={(value) => {
									setValue(`creditType`, value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch(`creditType`)}
								viewMode={isViewMode}
							/>
						</FormItem>
					)}

					<FormItem colSpan={defaultColSpan} label="pageAvailable" isLoading={isLoading}>
						<StringInput name="pageAvailable" placeholder="pageAvailable" viewMode={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="validFrom" isLoading={isLoading} required>
						<DateInput
							name="validFrom"
							value={watch('validFrom')}
							enableTime
							setValue={(value) => {
								setValue('validFrom', value, { shouldValidate: true, shouldDirty: true });
							}}
							viewMode={isViewMode}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="expiry" isLoading={isLoading} required>
						<DateInput
							name="expiry"
							value={watch('expiry')}
							enableTime
							setValue={(value) => {
								setValue('expiry', value, { shouldValidate: true, shouldDirty: true });
							}}
							viewMode={isViewMode}
						/>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="oneTime" isLoading={isLoading} required>
						<SwitchInput name="oneTime" viewMode={isViewMode} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="usageLimit" isLoading={isLoading}>
						<IntegerInput name="usageLimit" allowFloat={false} showArrows={false} viewMode={isViewMode} />
					</FormItem>

					<S.FormSubHeader>
						<Typography fontSize="1.8rem" fontWeight="600">
							creditDetails
						</Typography>
					</S.FormSubHeader>
					{[PROMO_CODE_TYPES.freeCredit, PROMO_CODE_TYPES.subscriptionDiscount].includes(promoCodeType) && (
						<FormItem colSpan={defaultColSpan} label="freeCreditAmount" isLoading={isLoading} required>
							<IntegerInput
								name="freeCreditAmount"
								allowFloat={false}
								showArrows={false}
								disabled={
									promoCodeType === PROMO_CODE_TYPES.subscriptionDiscount && watch('percentage')
								}
								viewMode={isViewMode}
							/>
						</FormItem>
					)}

					{[PROMO_CODE_TYPES.freeCredit, PROMO_CODE_TYPES.buyXGetXFree].includes(promoCodeType) && (
						<>
							<FormItem colSpan={defaultColSpan} label="creditExpiryDate" isLoading={isLoading}>
								<DateInput
									name="creditExpiryDate"
									value={watch('creditExpiryDate')}
									enableTime
									setValue={(value) => {
										setValue('creditExpiryDate', value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									viewMode={isViewMode}
									disabled={watch('creditExpiryDays')}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="creditExpiryDays" isLoading={isLoading}>
								<IntegerInput
									name="creditExpiryDays"
									allowFloat={false}
									showArrows={false}
									viewMode={isViewMode}
									disabled={watch('creditExpiryDate')}
								/>
							</FormItem>
						</>
					)}

					{promoCodeType === PROMO_CODE_TYPES.buyXGetXFree && (
						<>
							<FormItem colSpan={defaultColSpan} label="minCreditPurchase" isLoading={isLoading}>
								<IntegerInput
									name="minCreditPurchase"
									allowFloat={false}
									showArrows={false}
									viewMode={isViewMode}
								/>
							</FormItem>
							<FormItem colSpan={defaultColSpan} label="maxFreeCredit" isLoading={isLoading}>
								<IntegerInput
									name="maxFreeCredit"
									allowFloat={false}
									showArrows={false}
									viewMode={isViewMode}
								/>
							</FormItem>
						</>
					)}

					{[PROMO_CODE_TYPES.buyXGetXFree, PROMO_CODE_TYPES.subscriptionDiscount].includes(promoCodeType) && (
						<>
							<FormItem colSpan={defaultColSpan} label="percentage" isLoading={isLoading} required>
								<IntegerInput
									name="percentage"
									allowFloat={false}
									showArrows={false}
									max={2}
									min={0}
									unit={'%'}
									unitPosition="left"
									onChange={({ target: { value } }) =>
										setValue('percentage', value > 100 ? 100 : value, {
											shouldValidate: true,
											shouldDirty: true
										})
									}
									disabled={
										promoCodeType === PROMO_CODE_TYPES.subscriptionDiscount &&
										watch('freeCreditAmount')
									}
									viewMode={isViewMode}
								/>
							</FormItem>
						</>
					)}

					{[PROMO_CODE_TYPES.freeSubscription, PROMO_CODE_TYPES.subscriptionDiscount].includes(
						promoCodeType
					) && (
						<>
							<FormItem
								colSpan={defaultColSpan}
								label="subscriptionPaymentType"
								isLoading={isLoading}
								required>
								<EnumSelect
									name="subscriptionPaymentType"
									enumName={ENUMS.SUBSCRIPTION_PAYMENT_TYPE}
									setValue={(value) => {
										setValue(`subscriptionPaymentType`, value, {
											shouldValidate: true,
											shouldDirty: true
										});
									}}
									value={watch(`subscriptionPaymentType`)}
									viewMode={isViewMode}
								/>
							</FormItem>
							<FormItem
								colSpan={defaultColSpan}
								label="subscriptionFreeDays"
								isLoading={isLoading}
								required={promoCodeType === PROMO_CODE_TYPES.freeSubscription}>
								<IntegerInput
									name="subscriptionFreeDays"
									allowFloat={false}
									showArrows={false}
									viewMode={isViewMode}
								/>
							</FormItem>
						</>
					)}

					<FormItem colSpan={defaultColSpan} label="clientRestriction" isLoading={isLoading}>
						<SwitchInput name="clientRestriction" viewMode={isViewMode} />
					</FormItem>

					{watch('clientRestriction') && (
						<FormItem colSpan={defaultColSpan} label="clientPickListTitle" isLoading={isLoading} required>
							<AutoComplete
								autoCompleteName={AUTOCOMPLETE.CLIENT_PICK_LIST}
								name="clientPickListTitle"
								value={watch('clientPickListTitle')}
								setValue={(value) =>
									setValue('clientPickListTitle', value?.map((item) => item.ID) ?? null, {
										shouldValidate: true,
										shouldDirty: true
									})
								}
								columns={[{ accessor: 'Name', width: 100 }]}
								selectionLabelAccessor={['ID', 'Name']}
								multipleSelection
								viewMode={isViewMode}
							/>
						</FormItem>
					)}

					<FormItem colSpan={defaultColSpan} label="packagePickListTitle" isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.PACKAGE_PICK_LIST}
							name="packagePickListTitle"
							value={watch('packagePickListTitle')}
							multipleSelection
							setValue={(value) =>
								setValue('packagePickListTitle', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								})
							}
							columns={[
								{ accessor: 'Name', width: 100 },
								{ accessor: 'CountryName', width: 100 }
							]}
							selectionLabelAccessor={['ID', 'CountryName', 'Name']}
							viewMode={isViewMode}
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label={isViewMode ? 'close' : 'cancel'} variant="outline" onClick={handleCloseModal} />
				{mode !== 'view' && (
					<Button
						loading={isLoading}
						label={mode === 'edit' ? 'save' : 'button.add'}
						disabled={!isDirty}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default PromoCodeForm;
